import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTimeClock } from '../hooks/useTimeClock';
import { useTheme } from '../hooks/useTheme';
import { TimecardSummaryData, EmployeeRole } from '../../shared/types.js';
import Button from '../components/Button';
import TimecardSummaryReport from '../components/TimecardSummaryReport';
import EntryEditor from '../components/EntryEditor';
import ConfigurationScreen from './ConfigurationScreen';
import { LogOutIcon, ChevronLeftIcon, LockIcon, SunIcon, MoonIcon, PrintIcon, DownloadIcon } from '../components/icons';
import { generateTimecardSummaryData, getDateRangeForPreset, correctDateForTimezone, exportToCsv } from '../utils.js';
import ReportCriteriaModal, { ReportCriteria } from '../components/ReportCriteriaModal';
import DetailReportGenerator from '../components/DetailReportGenerator';
import ClosePeriodModal from '../components/ClosePeriodModal';

const SummaryReportGenerator: React.FC = () => {
    const { employees, timeLogs, activities, overtimeRules, timeSettings, leaveEntries, leaveCategories, payrollSettings, displayGroups } = useTimeClock();
    const [isModalOpen, setIsModalOpen] = useState(false);
	const [reportData, setReportData] = useState<TimecardSummaryData | null>(null);
    const [currentCriteria, setCurrentCriteria] = useState<ReportCriteria | null>(null);
    const employeesInAnyGroup = useMemo(() => {
        const groupEmployeeIds = new Set(displayGroups.flatMap(g => g.employeeIds));
        return employees.filter(e => groupEmployeeIds.has(e.id));
    }, [employees, displayGroups]);
    const activeEmployeesInAnyGroup = useMemo(() => employeesInAnyGroup.filter(e => e.role !== EmployeeRole.INACTIVE), [employeesInAnyGroup]);
    const handleGenerateReport = useCallback((criteria: ReportCriteria) => {
        const { employeeIds, startDate, endDate } = criteria;
        const start = correctDateForTimezone(startDate);
        start.setHours(0,0,0,0);
        const end = correctDateForTimezone(endDate);
        end.setHours(23,59,59,999);
        const selectedEmployees = employees
            .filter(emp => employeeIds.includes(emp.id))
            .sort((a, b) => (a.lastName || '').localeCompare(b.lastName || '') || (a.firstName || '').localeCompare(b.firstName || ''));
        const data = generateTimecardSummaryData(selectedEmployees, timeLogs, leaveEntries, leaveCategories, activities, start, end, overtimeRules, timeSettings);
        const filteredSummaries = data.employeeSummaries.filter(summary => summary.totalPaid > 0 || summary.unpaid > 0);
        const newTotals = filteredSummaries.reduce((acc, summary) => {
            acc.unpaid += summary.unpaid;
            acc.regular += summary.regular;
            acc.ot += summary.ot;
            acc.totalPaid += summary.totalPaid;
            return acc;
        }, { unpaid: 0, regular: 0, ot: 0, totalPaid: 0 });
        const filteredReportData: TimecardSummaryData = {
            ...data,
            employeeSummaries: filteredSummaries,
            totals: newTotals
        };
        setReportData(filteredReportData);
        setCurrentCriteria(criteria);
        setIsModalOpen(false);
    }, [employees, timeLogs, activities, leaveEntries, leaveCategories, overtimeRules, timeSettings]);

     useEffect(() => {
        if (!currentCriteria && activeEmployeesInAnyGroup.length > 0) {
            const defaultEmployeeIds = activeEmployeesInAnyGroup.map(e => e.id);
            const { start, end } = getDateRangeForPreset('this_period', payrollSettings);
            handleGenerateReport({
                employeeIds: defaultEmployeeIds,
                startDate: start,
                endDate: end
            });
        }
    }, [activeEmployeesInAnyGroup, payrollSettings, currentCriteria, handleGenerateReport]);
    const handleExportCsv = useCallback(() => {
        if (reportData && reportData.employeeSummaries.length > 0) {
            const dataToExport = reportData.employeeSummaries.map(summary => ({
                'Employee Number': summary.employeeNumber,
                'Employee Name': summary.employeeName,
                'Unpaid Hours': summary.unpaid.toFixed(2),
                'Regular Hours': summary.regular.toFixed(2),
                'OT Hours': summary.ot.toFixed(2),
                'Total Paid Hours': summary.totalPaid.toFixed(2),
            }));
            exportToCsv(dataToExport, `summary_report_${reportData.dateRange.replace(/\//g, '-').replace(/ /g, '')}.csv`);
        } else {
            alert('No data to export.');
        }
    }, [reportData]);
	const handlePrint = () => {
        window.print();
    };
    return (
        <div>
            <div className="flex flex-wrap items-center gap-4 mb-6 bg-white dark:bg-gray-800 p-4 rounded-lg shadow-lg select-none">
                 <p className="text-gray-600 dark:text-gray-300 flex-grow">Generate a summary of paid hours for multiple employees.</p>
                <Button variant="secondary" onClick={handleExportCsv} disabled={!reportData || reportData.employeeSummaries.length === 0}>
                    <DownloadIcon/> Export to CSV
                </Button>
                <Button variant="secondary" onClick={handlePrint}>
                    <PrintIcon/> Print Report
                </Button>
				<Button onClick={() => setIsModalOpen(true)}>
                    {currentCriteria ? 'Change Employees & Date Range' : 'Select Employees & Date Range'}
                </Button>
            </div>

             <ReportCriteriaModal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                onGenerate={handleGenerateReport}
                employees={employeesInAnyGroup}
                title="Generate Summary Report"
                initialCriteria={currentCriteria}
            />
            {reportData && <TimecardSummaryReport data={reportData} />}
        </div>
    );
};

const AdminScreen: React.FC = () => {
	const location = useLocation();
    const navState = location.state as { initialTab?: string; filterEmployeeId?: string } | null;
    const [activeTab, setActiveTab] = useState(navState?.initialTab || 'entryEditor');
    const navigate = useNavigate();
	const { theme, toggleTheme } = useTheme();
    const { closePeriod, payrollSettings } = useTimeClock();
    const [configView, setConfigView] = useState<string | null>(null);
	const [isClosePeriodModalOpen, setIsClosePeriodModalOpen] = useState(false);
    useEffect(() => {
      if (navState?.initialTab && navState.initialTab !== activeTab) {
        setActiveTab(navState.initialTab);
      }
    }, [navState, activeTab]);
    const handleLogout = () => {
        localStorage.removeItem('isAdminAuthenticated');
        navigate('/');
    };
    const handleReturnToKiosk = () => {
        navigate('/');
    };
    const handleClosePeriodConfirm = async () => {
        await closePeriod();
        setIsClosePeriodModalOpen(false);
        alert('Period closed successfully. The new payroll period has started.');
    };
    return (
        <div className="min-h-screen bg-gray-100 dark:bg-gray-900 p-4 md:p-8 select-none">
            <div className="max-w-7xl mx-auto">
                <div className="flex flex-col md:flex-row justify-between items-center mb-8 gap-4">
                    <h1 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white">Admin Dashboard</h1>
                    <div className="flex items-center gap-3 flex-wrap sm:flex-nowrap">
                        <Button variant="secondary" onClick={handleReturnToKiosk} className="w-full sm:w-auto text-sm md:text-base px-3 py-2 whitespace-nowrap">
                            <ChevronLeftIcon className="w-5 h-5" />
                            Return to Kiosk
                        </Button>
						<Button variant="secondary" onClick={() => setIsClosePeriodModalOpen(true)} className="w-full sm:w-auto text-sm md:text-base px-3 py-2 whitespace-nowrap">
							<LockIcon className="w-5 h-5" />
							Close Period
						</Button>
                        <Button variant="danger" onClick={handleLogout} className="w-full sm:w-auto text-sm md:text-base px-3 py-2">
                            <LogOutIcon className="w-5 h-5" />
                            Logout
                        </Button>
						<button
							onClick={toggleTheme}
							className="flex items-center justify-center p-2 md:p-2.5 rounded-full text-gray-500 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors duration-200 text-sm md:text-base"
							aria-label="Toggle theme"
						>
							{theme === 'light' ? <MoonIcon className="w-5 h-5"/> : <SunIcon className="w-5 h-5"/>}
						</button>
                    </div>
                </div>
                <div className="flex flex-wrap gap-2 border-b border-gray-200 dark:border-gray-700 mb-6">
                    <button className={`py-2 px-3 text-sm md:text-lg rounded-md md:rounded-none md:rounded-t-md ${activeTab === 'configuration' ? 'text-brand-secondary md:border-b-2 md:border-brand-secondary bg-gray-100 dark:bg-gray-800 md:bg-transparent' : 'text-gray-500 dark:text-gray-400'}`} onClick={() => setActiveTab('configuration')}>Configuration</button>
                    <button className={`py-2 px-3 text-sm md:text-lg rounded-md md:rounded-none md:rounded-t-md ${activeTab === 'entryEditor' ? 'text-brand-secondary md:border-b-2 md:border-brand-secondary bg-gray-100 dark:bg-gray-800 md:bg-transparent' : 'text-gray-500 dark:text-gray-400'}`} onClick={() => setActiveTab('entryEditor')}>Entry Editor</button>
                    <button className={`py-2 px-3 text-sm md:text-lg rounded-md md:rounded-none md:rounded-t-md ${activeTab === 'detailReport' ? 'text-brand-secondary md:border-b-2 md:border-brand-secondary bg-gray-100 dark:bg-gray-800 md:bg-transparent' : 'text-gray-500 dark:text-gray-400'}`} onClick={() => setActiveTab('detailReport')}>Detail Report</button>
                    <button className={`py-2 px-3 text-sm md:text-lg rounded-md md:rounded-none md:rounded-t-md ${activeTab === 'summaryReport' ? 'text-brand-secondary md:border-b-2 md:border-brand-secondary bg-gray-100 dark:bg-gray-800 md:bg-transparent' : 'text-gray-500 dark:text-gray-400'}`} onClick={() => setActiveTab('summaryReport')}>Summary Report</button>
                </div>
                {activeTab === 'configuration' && <ConfigurationScreen initialView={configView} onNavigated={() => setConfigView(null)}/>}
                {activeTab === 'entryEditor' && <EntryEditor />}
                {activeTab === 'detailReport' && <DetailReportGenerator initialFilterEmployeeId={navState?.filterEmployeeId} />}
                {activeTab === 'summaryReport' && <SummaryReportGenerator />}
            </div>
			<ClosePeriodModal
                isOpen={isClosePeriodModalOpen}
                onClose={() => setIsClosePeriodModalOpen(false)}
                onConfirm={handleClosePeriodConfirm}
                payrollSettings={payrollSettings}
            />
        </div>
    );
};

export default AdminScreen;